import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Bot, RefreshCw, Play, Power, PowerOff,
  CheckCircle, XCircle, Clock, AlertTriangle,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  request_missing_info:    "Request missing info",
  search_flights:          "Search flights",
  review_existing_results: "Review existing results",
  prepare_options:         "Prepare options",
  flag_manual:             "Flag for manual handling",
};

const ACTION_COLORS = {
  request_missing_info:    "bg-amber-100 text-amber-800",
  search_flights:          "bg-blue-100 text-blue-800",
  review_existing_results: "bg-purple-100 text-purple-800",
  prepare_options:         "bg-green-100 text-green-800",
  flag_manual:             "bg-red-100 text-red-800",
};

function StatusDot({ status }) {
  if (status === "completed") return <CheckCircle className="w-4 h-4 text-emerald-500" />;
  if (status === "failed")    return <XCircle className="w-4 h-4 text-red-500" />;
  return <Clock className="w-4 h-4 text-amber-500 animate-pulse" />;
}

function RunRow({ run }) {
  const [expanded, setExpanded] = useState(false);
  const analysis = run.analysis || {};
  const actionLabel = ACTION_LABELS[analysis.recommended_action] || analysis.recommended_action || "—";
  const actionColor = ACTION_COLORS[analysis.recommended_action] || "bg-slate-100 text-slate-700";

  const startedAt = run.started_at
    ? format(typeof run.started_at === "string" ? parseISO(run.started_at) : new Date(run.started_at * 1000), "dd MMM HH:mm")
    : "—";

  return (
    <>
      <tr
        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
      >
        <td className="py-2.5 px-4 text-sm font-mono text-slate-700">
          {run.request_number || "—"}
        </td>
        <td className="py-2.5 px-4">
          <div className="flex items-center gap-1.5">
            <StatusDot status={run.status} />
            <span className="text-sm capitalize text-slate-600">{run.status}</span>
          </div>
        </td>
        <td className="py-2.5 px-4 text-sm text-slate-500 max-w-xs truncate">
          {analysis.summary || (run.error ? <span className="text-red-500">{run.error}</span> : "—")}
        </td>
        <td className="py-2.5 px-4">
          {analysis.recommended_action
            ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${actionColor}`}>{actionLabel}</span>
            : <span className="text-slate-400 text-xs">—</span>}
        </td>
        <td className="py-2.5 px-4 text-xs text-slate-400">{startedAt}</td>
        <td className="py-2.5 px-4 text-slate-400">
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50">
          <td colSpan={6} className="px-4 py-3">
            {run.status === "failed" ? (
              <p className="text-sm text-red-600"><strong>Error:</strong> {run.error}</p>
            ) : analysis.summary ? (
              <div className="space-y-2 text-sm">
                <p className="text-slate-700"><strong>Summary:</strong> {analysis.summary}</p>
                {analysis.recommended_action_reason && (
                  <p className="text-slate-600"><strong>Reason:</strong> {analysis.recommended_action_reason}</p>
                )}
                {analysis.missing_fields && analysis.missing_fields.length > 0 && (
                  <div>
                    <strong className="text-slate-700">Missing fields:</strong>
                    <ul className="ml-4 mt-1 list-disc text-slate-600">
                      {analysis.missing_fields.map((mf, i) => (
                        <li key={i}>{mf.field} — {mf.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.issues && analysis.issues.length > 0 && (
                  <div>
                    <strong className="text-slate-700">Issues:</strong>
                    <ul className="ml-4 mt-1 list-disc text-slate-600">
                      {analysis.issues.map((iss, i) => (
                        <li key={i}>
                          <span className={iss.severity === "error" ? "text-red-600" : "text-amber-600"}>
                            [{iss.severity.toUpperCase()}]
                          </span>{" "}
                          {iss.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.suggested_customer_message_draft && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs font-semibold text-amber-700 mb-1">
                      Suggested message draft — FOR HUMAN REVIEW ONLY — not sent automatically
                    </p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {analysis.suggested_customer_message_draft}
                    </p>
                  </div>
                )}
                <div className="flex gap-4 text-xs text-slate-500 pt-1">
                  <span>Completeness: <strong>{analysis.completeness_score ?? "—"}/100</strong></span>
                  <span>Confidence: <strong>{analysis.confidence ?? "—"}/100</strong></span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">No analysis data.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function AgentActivity() {
  const { getAuthHeaders, agent: currentAgent } = useAuth();
  const isAdmin = currentAgent?.role === "admin";

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agent/status`, { headers: getAuthHeaders() });
      setStatus(res.data);
    } catch {
      toast.error("Failed to load agent status");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleToggle = async () => {
    if (!isAdmin) return;
    setToggling(true);
    try {
      const res = await axios.patch(
        `${API}/agent/config`,
        { enabled: !status.enabled },
        { headers: getAuthHeaders() }
      );
      setStatus(s => ({ ...s, enabled: res.data.enabled }));
      toast.success(res.data.enabled ? "Agent enabled" : "Agent disabled");
    } catch {
      toast.error("Failed to update agent config");
    } finally {
      setToggling(false);
    }
  };

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/agent/trigger-run`, {}, { headers: getAuthHeaders() });
      toast.success(`Run complete: ${res.data.processed} processed, ${res.data.errors} errors`);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Trigger failed");
    } finally {
      setTriggering(false);
    }
  };

  const runs = status?.recent_runs || [];
  const failedRuns = runs.filter(r => r.status === "failed");

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 rounded-xl">
              <Bot className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-800">AI Agent Activity</h1>
              <p className="text-sm text-slate-500">Flight request analysis · Phase 1</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchStatus}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Refresh
            </Button>
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTrigger}
                  disabled={triggering}
                >
                  <Play className="w-3.5 h-3.5 mr-1.5" />
                  {triggering ? "Running…" : "Run now"}
                </Button>
                <Button
                  variant={status?.enabled ? "destructive" : "default"}
                  size="sm"
                  onClick={handleToggle}
                  disabled={toggling}
                >
                  {status?.enabled
                    ? <><PowerOff className="w-3.5 h-3.5 mr-1.5" />Disable</>
                    : <><Power className="w-3.5 h-3.5 mr-1.5" />Enable</>}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Status cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className={`border ${status?.enabled ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`}>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Status</p>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${status?.enabled ? "bg-emerald-500" : "bg-slate-400"}`} />
                <span className="text-sm font-semibold text-slate-700">
                  {status?.enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Pending requests</p>
              <p className="text-2xl font-bold text-slate-800">{status?.pending_request_count ?? "—"}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Recent runs</p>
              <p className="text-2xl font-bold text-slate-800">{runs.length}</p>
            </CardContent>
          </Card>
          <Card className={`border ${failedRuns.length > 0 ? "border-red-200 bg-red-50/50" : "border-slate-200"}`}>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Failed runs</p>
              <div className="flex items-center gap-2">
                {failedRuns.length > 0 && <AlertTriangle className="w-4 h-4 text-red-500" />}
                <p className={`text-2xl font-bold ${failedRuns.length > 0 ? "text-red-600" : "text-slate-800"}`}>
                  {failedRuns.length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Configuration info */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm text-slate-600">
              <span><strong>Model:</strong> {status?.model || "—"}</span>
              <span><strong>Loop interval:</strong> {status?.loop_interval_seconds}s</span>
              <span><strong>Authority:</strong> Read-only analysis + internal notes</span>
              <span><strong>Customer emails:</strong> Requires human approval</span>
            </div>
          </CardContent>
        </Card>

        {/* Runs table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-slate-700">Recent runs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {runs.length === 0 ? (
              <p className="p-6 text-sm text-slate-400 text-center">No runs yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="py-2.5 px-4 text-xs font-medium text-slate-500 uppercase">Request</th>
                      <th className="py-2.5 px-4 text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="py-2.5 px-4 text-xs font-medium text-slate-500 uppercase">Summary</th>
                      <th className="py-2.5 px-4 text-xs font-medium text-slate-500 uppercase">Action</th>
                      <th className="py-2.5 px-4 text-xs font-medium text-slate-500 uppercase">Started</th>
                      <th className="py-2.5 px-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map(run => <RunRow key={run.id} run={run} />)}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Authority notice */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <strong>Phase 1 authority:</strong> The agent analyses flight requests and appends internal notes.
          It does not send customer emails, create bookings, issue tickets, create invoices, or modify financial
          records. All such actions require explicit human approval and are not yet implemented.
        </div>
      </div>
    </DashboardLayout>
  );
}
